import type { ReactNode } from 'react'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import { RECOMMENDATION_CAROUSEL_CARD_OUTER_WIDTH } from './PlanRecommendationFundTile'
import { RecommendationFundTileHighlight } from './RecommendationFundTileHighlight'
import {
  RECOMMENDATION_FUNDS_SECTION_INSET_PX,
  RecommendationFundsSectionTitle
} from './RecommendationFundsSectionTitle'

type RecommendationFundsCarouselFund = {
  isin?: string | null
  fundName?: string | null
  executed?: boolean | null
}

type RecommendationFundsCarouselSectionProps<Fund extends RecommendationFundsCarouselFund> = {
  title: string
  funds: Fund[]
  onSelect: (selectedIndex: number) => void
  sectionClassName?: string
  type: LumpsumRecommendationActionType
  highlightIsin?: string
  renderFundCard: (fund: Fund) => ReactNode
  analyticsScreen?: string
}

export function RecommendationFundsCarouselSection<Fund extends RecommendationFundsCarouselFund>({
  title,
  funds,
  onSelect,
  sectionClassName = '',
  type,
  highlightIsin = '',
  renderFundCard,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: RecommendationFundsCarouselSectionProps<Fund>) {
  const scrollSpacerTrailingStyle = `max(${RECOMMENDATION_FUNDS_SECTION_INSET_PX}px, calc(100cqw - ${RECOMMENDATION_CAROUSEL_CARD_OUTER_WIDTH} - ${RECOMMENDATION_FUNDS_SECTION_INSET_PX}px))`
  const completedCount = funds.filter((fund) => fund?.executed === true).length

  return (
    <div className={sectionClassName}>
      <RecommendationFundsSectionTitle title={title} type={type} completedCount={completedCount} />
      <div
        style={{ containerType: 'inline-size' }}
        className="overflow-x-auto overflow-y-hidden no-scrollbar scroll-pe-5"
      >
        <div
          className="flex gap-3 pr-5"
          style={{ paddingInlineStart: RECOMMENDATION_FUNDS_SECTION_INSET_PX }}
        >
          {funds.map((fund, fundIndex) => {
            const fundIsin = fund?.isin ?? ''
            const fundName = fund?.fundName ?? ''
            return (
              <div
                key={`${fundIsin}-${fundIndex}`}
                className="shrink-0"
                style={{ width: RECOMMENDATION_CAROUSEL_CARD_OUTER_WIDTH }}
                onClick={() => {
                  trackEvent({
                    eventName: EventNames.UserClicked,
                    payload: {
                      ScreenName: analyticsScreen,
                      eventId: PlansLumpsumSheetEventIds.CarouselFund,
                      buttonLabel: fundName || fundIsin,
                      ...(fundName ? { fundName } : {}),
                      ...(fundIsin ? { fundIsin } : {})
                    }
                  })
                  onSelect(fundIndex)
                }}
              >
                <RecommendationFundTileHighlight
                  isHighlighted={highlightIsin.length > 0 && fundIsin === highlightIsin}
                >
                  {renderFundCard(fund)}
                </RecommendationFundTileHighlight>
              </div>
            )
          })}
          <div
            aria-hidden="true"
            className="shrink-0 pointer-events-none"
            style={{
              flex: 'none',
              width: scrollSpacerTrailingStyle
            }}
          />
        </div>
      </div>
    </div>
  )
}
